import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import type { ApiError } from '@/api/http';
import { queryKeyRoots } from '@/api/queryKeys';
import { updateRole, type Role } from '@/api/vacancies';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/Dialog';

interface Props {
  vacancy: Role;
  onClose: () => void;
}

/**
 * §6.8's **Close** — and its **Reopen**, which is the same request with the other boolean.
 *
 * ⚠️ **This is what "delete" means for a vacancy** (ADR 0005, **feedback protection #2**). There is no
 * delete: `PATCH {"is_open": false}` is the whole of it, `RoleUpdate`'s docstring says so in as many
 * words (*"that is what 'delete' means for a vacancy, which is not deletable through any surface"*),
 * and the row, its questions, its candidates and every interview and score recorded against it stay.
 * This screen is the first place in the product that could have carried a *Delete vacancy* button,
 * which is why `vacancyWrites.test.tsx` asserts none exists **while this dialog is open**.
 *
 * ⚠️ **What the confirmation says, and why it does not say "removed".** Closing is not a soft delete
 * and the sentences must not imply the vacancy is going away — it stays in the catalogue carrying the
 * word *Closed* (§6.8, TASK-071), so a confirmation promising removal would contradict the screen it
 * returns to about a fact the reader can see two seconds later. Three things are stated instead:
 *
 *  1. **what stays** — the row in the catalogue, its questions, its candidates, their feedback;
 *  2. **that closing marks rather than locks** — a closed vacancy *still* accepts a new candidate on
 *     the server (`apps/people/tests/test_considerations_api.py::
 *     test_a_closed_vacancy_still_accepts_a_new_consideration`: *"refusing would be hiring policy"*),
 *     so a confirmation claiming it stops anything would be false. It records that you are no longer
 *     hiring for the position;
 *  3. **that it is reversible** — the same menu reopens it.
 *
 * ⚠️ **Deliberately much lighter than `AnonymizeDialog`, and that is a decision rather than an
 * omission.** `is_open` is a boolean on a `PATCH`: there is no dire property to acknowledge, so there
 * is **no checkbox to tick, no `danger` button and no "cannot be undone"** here. An over-heavy
 * confirmation on a reversible act trains people to click through the one that matters — and the one
 * that matters is one screen away (§6.5's erasure, ADR 0009, which has no undo anywhere). What this
 * dialog *is* for is that closing is not a silent toggle: the sentences above are what a menu item
 * firing a write immediately could not say.
 *
 * **Reopen is a confirmation too**, in the same shape and shorter. Two reasons, neither of them
 * symmetry for its own sake: a write fired straight from a menu item has nowhere to report a 403, a
 * throttle or a lost connection — it would fail silently — and the reader is owed the same one line
 * about what reopening does and does not change.
 */
export function CloseVacancyDialog({ vacancy, onClose }: Props) {
  const { t } = useTranslation(['vacancies', 'common', 'components']);
  const queryClient = useQueryClient();

  /** Which direction this is. The vacancy as it stands decides; the dialog offers only the other one. */
  const closing = vacancy.is_open;

  const write = useMutation<Role, ApiError, void>({
    /**
     * **One key in the body, and it is never `null`.** `RoleUpdate` is omittable rather than nullable
     * (an explicit `null` is a 422), and nothing else about the vacancy is being changed here — a
     * body that re-sent the title would make this write depend on a payload that may already be
     * stale.
     */
    mutationFn: () => updateRole(vacancy.id, { is_open: !closing }),
    onSuccess: () => {
      /**
       * One root for every entry about this vacancy (`queryKeys.roles`): the catalogue page the
       * grid is showing, and anything narrower filed under the same family — invalidating by
       * **root** is what makes that true without naming a key. (It named `queryKeys.role(id)` here
       * until 2026-09-28; that builder had no query behind it and went with BUG-079, and this call
       * is unchanged by its going, which is the point of invalidating a family rather than a key.)
       * The answer is thrown away rather than written into the cache — the grid's own refetch is
       * what puts the word *Closed* on the cell, and the order (`-is_open, title, id`) is the
       * **API's**, so a hand-patched cache entry would leave a closed vacancy sitting among the
       * open ones until a reload.
       */
      void queryClient.invalidateQueries({ queryKey: queryKeyRoots.roles });
      onClose();
    },
  });

  /** §8 in a dialog: what failed, in words. See `VacancyDialog` for why offline says its own line. */
  const failure = (() => {
    if (!write.error) return null;
    if (write.error.isOffline) return t('vacancies:dialog.offline');
    if (write.error.isThrottled) return t('components:state.throttled');
    return t(closing ? 'vacancies:close.failed' : 'vacancies:reopen.failed');
  })();

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      // Named, because the failure mode of a row action is doing it to the wrong row.
      title={t(closing ? 'vacancies:close.title' : 'vacancies:reopen.title', {
        title: vacancy.title,
      })}
      description={t(closing ? 'vacancies:close.description' : 'vacancies:reopen.description')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common:cancel')}
          </Button>
          {/*
           * ⚠️ **The ordinary primary button, not `danger`.** `danger` is outlined in `negative` and
           * §4 reserves it for what it cannot take back; wearing it here would make a reversible flag
           * look like §6.5's erasure. It is not autofocused either — `Dialog` puts focus on the
           * content (§9) — so *Close vacancy* is never one Enter away.
           */}
          <Button onClick={() => write.mutate()} isLoading={write.isPending}>
            {t(closing ? 'vacancies:close.submit' : 'vacancies:reopen.submit')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3 text-sm text-ink-900">
        {/* What stays — stated first, because it is what the reader is actually asking about. */}
        <p>{t(closing ? 'vacancies:close.stays' : 'vacancies:reopen.stays')}</p>

        {/* Closing marks the position; it locks nothing. Only true of closing, so only said there. */}
        {closing && <p>{t('vacancies:close.notALock')}</p>}

        <p className="text-ink-700">
          {t(closing ? 'vacancies:close.reversible' : 'vacancies:reopen.reversible')}
        </p>

        {failure && (
          <p role="alert" className="text-negative">
            {failure}
          </p>
        )}
      </div>
    </Dialog>
  );
}
